import Vue from 'vue'
import VueRouter from 'vue-router'
import { isLoggedIn, isSetupComplete, getSetupStep } from '../utils/auth'
import MessagesView from '../views/MessagesView.vue'

Vue.use(VueRouter)

// 抑制路由切换被取消/重复时的 Promise rejection 错误
// Vue Router 3 通过 err._isRouter 标记路由内部错误，而非 err.name
const isNavFailure = err => err && err._isRouter
const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => {
    if (!isNavFailure(err)) throw err
  })
}
const originalReplace = VueRouter.prototype.replace
VueRouter.prototype.replace = function replace(location) {
  return originalReplace.call(this, location).catch(err => {
    if (!isNavFailure(err)) throw err
  })
}

const routes = [
  // ── Auth & Onboarding ──────────────────────────────────
  {
    path: '/auth',
    name: 'auth',
    component: () => import('../views/AuthView.vue'),
    alias: '/login',  // SSO 回跳兼容：后端 OIDC 文档里写的 /#/login 也走同一个登录页
    meta: { public: true }
  },
  {
    path: '/setup/employee',
    name: 'setup-employee',
    component: () => import('../views/setup/EmployeeSetupView.vue'),
    meta: { requiresAuth: true, setupStep: 2 }
  },
  {
    path: '/setup/assistant',
    name: 'setup-assistant',
    component: () => import('../views/setup/AssistantSetupView.vue'),
    meta: { requiresAuth: true, setupStep: 3 }
  },
  {
    // 入职审核中(pending 受限 token):不挂 setupStep/requiresSetup,避免被守卫弹走
    path: '/setup/pending',
    name: 'setup-pending',
    component: () => import('../views/setup/PendingApprovalView.vue'),
    meta: { requiresAuth: true }
  },
  {
    // 申请被驳回:无 token 也能看
    path: '/setup/rejected',
    name: 'setup-rejected',
    component: () => import('../views/setup/RejectedView.vue'),
    meta: { public: true }
  },

  // ── Share accept landing ───────────────────────────────
  {
    path: '/c/:token',
    name: 'share-accept',
    component: () => import('../views/ShareAcceptView.vue'),
    meta: { public: true }
  },
  {
    path: '/wiki-share/:key',
    name: 'wiki-share-accept',
    component: () => import('../views/WikiShareAcceptView.vue'),
    meta: { public: true }
  },

  // ── Markdown Editor (persistent in App.vue, route only for URL/auth) ──
  {
    path: '/collab/markdown/:id',
    name: 'markdown-editor',
    component: { render(h) { return h() } },
    meta: { requiresAuth: true, requiresSetup: true }
  },
  // ── Table Viewer (persistent in App.vue, route only for URL/auth) ──
  {
    path: '/collab/table/:id',
    name: 'table-viewer',
    component: { render(h) { return h() } },
    meta: { requiresAuth: true, requiresSetup: true }
  },

  // ── Main App Routes ────────────────────────────────────
  { path: '/', redirect: '/messages' },
  {
    path: '/messages',
    name: 'messages',
    component: MessagesView,
    meta: { requiresAuth: true, requiresSetup: true }
  },
  // ── Dev (持久化嵌入 Studio iframe，组件挂在 App.vue，route 只占位) ──
  {
    path: '/dev',
    name: 'dev',
    component: { render(h) { return h() } },
    meta: { requiresAuth: true, requiresSetup: true }
  },
  {
    path: '/outputs',
    name: 'outputs',
    component: () => import('../views/OutputsView.vue'),
    meta: { requiresAuth: true, requiresSetup: true }
  },
  {
    path: '/schedules',
    name: 'schedules',
    component: () => import('../views/SchedulesView.vue'),
    meta: { requiresAuth: true, requiresSetup: true }
  },
  {
    path: '/collab',
    name: 'collab',
    component: () => import('../views/CollabView.vue'),
    meta: { requiresAuth: true, requiresSetup: true }
  },
  {
    path: '/knowledge',
    name: 'knowledge',
    component: () => import('../views/KnowledgeView.vue'),
    meta: { requiresAuth: true, requiresSetup: true }
  },
  {
    path: '/apps',
    name: 'apps',
    component: () => import('../views/AppCenterView.vue'),
    meta: { requiresAuth: true, requiresSetup: true }
  },
  {
    path: '/workspace',
    name: 'workspace',
    component: () => import('../views/WorkspaceView.vue'),
    meta: { requiresAuth: true, requiresSetup: true }
  },
  {
    path: '/experts',
    name: 'experts',
    component: () => import('../views/ExpertsView.vue'),
    meta: { requiresAuth: true, requiresSetup: true }
  },
  {
    path: '/skills',
    name: 'skills',
    component: () => import('../views/SkillsView.vue'),
    meta: { requiresAuth: true, requiresSetup: true }
  },
  {
    path: '/team',
    name: 'team',
    component: () => import('../views/TeamView.vue'),
    meta: { requiresAuth: true, requiresSetup: true }
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('../views/SettingsView.vue'),
    meta: { requiresAuth: true, requiresSetup: true }
  }
]

const router = new VueRouter({ routes })

// ── 导航守卫 ────────────────────────────────────────────
router.beforeEach((to, from, next) => {
  // 入职审核中/被驳回:用户关闭页面后重新打开,要继续等待审核或回到驳回页,
  // 否则因 setupStep=4 会被直接放进 /messages,但 agent 其实还没领取
  if (isLoggedIn()) {
    let approvalStatus = ''
    try {
      const s = JSON.parse(localStorage.getItem('mcn_auth') || 'null')
      approvalStatus = s && s.approvalStatus
    } catch (_) { /* ignore */ }
    if (approvalStatus === 'pending' && to.path !== '/setup/pending' && to.path !== '/auth') {
      return next('/setup/pending')
    }
    if (approvalStatus === 'rejected' && to.path !== '/setup/rejected' && to.path !== '/auth') {
      return next('/setup/rejected')
    }
  }

  // 公开页面直接放行（已登录则跳过登录页）
  if (to.meta.public) {
    if (isLoggedIn() && to.name === 'auth') {
      // 例外：如果 URL 里带 ?redirect=（SSO 回跳场景），放行给 AuthView 处理 token 跳回
      const hasSsoRedirect = /[?&]redirect=/.test(window.location.hash) || /[?&]redirect=/.test(window.location.search)
      if (!hasSsoRedirect) return next(isSetupComplete() ? '/messages' : setupRedirect())
    }
    return next()
  }

  // 未登录 → 跳转登录（保留原 URL，AuthView 登完跳回）
  if (!isLoggedIn()) {
    try {
      if (to.fullPath && to.fullPath !== '/auth' && !/^\/auth(\?|$)/.test(to.fullPath)) {
        sessionStorage.setItem('shareAcceptReturn', to.fullPath)
      }
    } catch (_) { /* sessionStorage 不可用就降级到原行为 */ }
    return next('/auth')
  }

  // 未完成 setup 且访问主应用 → 跳转对应 setup 步骤
  if (to.meta.requiresSetup && !isSetupComplete()) {
    return next(setupRedirect())
  }

  // setup 步骤页：检查是否已完成该步
  if (to.meta.setupStep !== undefined) {
    const step = getSetupStep()
    if (step > to.meta.setupStep + 1) return next('/messages')  // 已完成更多步骤
  }

  next()
})

function setupRedirect() {
  // V2 二步：未完成时只剩岗位+姓名要填
  return '/setup/employee'
}

// 登录后预加载所有页面 chunk，消除首次切换延迟
// MessagesView 最重要，立即预加载；其余等空闲
router.afterEach(() => {
  if (!prefetched) {
    prefetched = true
    // 其余页面空闲时加载（MessagesView 已同步打包）
    const loadRest = () => {
      import('../views/OutputsView.vue')
      import('../views/SchedulesView.vue')
      import('../views/WorkspaceView.vue')
      import('../views/ExpertsView.vue')
      import('../views/SkillsView.vue')
      import('../views/TeamView.vue')
      import('../views/SettingsView.vue')
      import('../views/KnowledgeView.vue')
      import('../views/AppCenterView.vue')
    }
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(loadRest)
    } else {
      setTimeout(loadRest, 800)
    }
  }
})

let prefetched = false

export default router
